import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout, CompanyInviteWidget, PersonWelcomeWidget, NotificationsWidget, ProjectTimelineWidget } from '../components';
import LinkedCompaniesWidget from '../components/LinkedCompaniesWidget';
import { OpenInvitations } from '../components/OpenInvitations';
import { Breadcrumb } from '../components/Breadcrumb';
import SectionPanel from '../components/SectionPanel';
import PersonTodoList, { type PersonTodoRow } from '../components/PersonTodoList';
import { getActivePersonTodos, type PersonTodo, type PersonTodoProfile } from '../utils/personTodos';
import { useOngoingObjections } from '../utils/objectionVariation';
import { useProjectFundingActions } from '../hooks/useProjectFundingActions';
import { useReportTodoCount } from '../navigation/todoCount';
import { useRefreshOnNotification } from '../hooks/useRefreshOnNotification';
import { TODO_ANCHOR } from '../navigation/appNavigation';
import { personApi, userApi, userPreferencesApi, projectStatusApi, contractApi, milestoneApi } from '../services/api';
import type { Contract, Milestone } from '../types';


interface LinkedCompanyInfo {
  companyNumber: number;
  companyName: string;
  companyPresentation: string | null;
  isSystemCompany: boolean;
  website?: string;
  email?: string;
  phone?: string;
  experienceYears?: number;
  specialties?: string[];
  languages?: string[];
  geographicArea?: string;
  hourlyRate?: string;
  responseTime?: string;
  leadTime?: string;
}

const shouldSyncSigning = (contract: Contract) =>
  !!contract.externalSigningProcessId &&
  ['sent', 'pending_signature'].includes(contract.signingStatus || '');

const syncSigningContracts = async (contracts: Contract[]) => {
  const syncedContracts = await Promise.all(
    contracts.map(async (contract) => {
      if (!shouldSyncSigning(contract)) return contract;
      try {
        const synced = await contractApi.syncSigning(contract.id);
        return synced.contract;
      } catch {
        return contract;
      }
    })
  );
  return syncedContracts;
};

export function PersonHome() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [companies, setCompanies] = useState<LinkedCompanyInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [projects, setProjects] = useState<any[]>([]);
  const [projectStatusMap, setProjectStatusMap] = useState<Record<string, any[]>>({});
  const [projectContractsMap, setProjectContractsMap] = useState<Record<string, Contract[]>>({});
  const [contractMilestonesMap, setContractMilestonesMap] = useState<Record<string, Milestone[]>>({});
  // Contact details for the "Komplettera din profil" to-do; null until loaded
  // so missing fields are never flagged early.
  const [profile, setProfile] = useState<PersonTodoProfile | null>(null);
  // Synchronous first-paint hint (per browser) so a returning collapsed user
  // renders minimized immediately — no "open then slide closed" flash. The
  // per-user server preference (loaded below) is authoritative. Open by
  // default when there is no hint (first-time visible).
  const [welcomeCollapsed, setWelcomeCollapsed] = useState(
    () => localStorage.getItem('personWelcomeWidgetCollapsedHint') === 'true'
  );
  // The slide transition is only enabled for explicit user toggles — never
  // when applying the loaded preference.
  const [welcomeAnimate, setWelcomeAnimate] = useState(false);
  // Per-contract funding state, kept fresh on payment events and when the
  // user returns to the tab — feeds the payment rows of "Att göra nu".
  const { actions: fundingActions, fundingByProject } = useProjectFundingActions(projects, projectContractsMap);

  useEffect(() => {
    loadCompanyInfo();
    userApi.getProfile().then(setProfile).catch(() => {});
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Projects, their status, contracts and stages. Loaded again when a new
  // notification arrives (e.g. the company sent a contract again), so the
  // page reflects it without a reload.
  const loadProjects = () => {
    return personApi.getProjects().then(data => {
      setProjects(data);
      // Fetch status instances for each project
      const statusPromises = data.map((p: any) =>
        projectStatusApi.getInstance(p.id)
          .then(instance => ({ projectId: p.id, steps: instance.steps || [] }))
          .catch(() => ({ projectId: p.id, steps: [] }))
      );
      Promise.all(statusPromises).then(results => {
        const map: Record<string, any[]> = {};
        results.forEach(r => { map[r.projectId] = r.steps; });
        setProjectStatusMap(map);
      });

      // Fetch contracts per project, then milestones per contract — used to
      // render one timeline per contract within each project widget.
      const contractPromises = data.map((p: any) =>
        contractApi.getContracts(p.id)
          .then(cs => ({ projectId: p.id, contracts: cs as Contract[] }))
          .catch(() => ({ projectId: p.id, contracts: [] as Contract[] }))
      );
      Promise.all(contractPromises).then(async results => {
        const syncedContracts = await syncSigningContracts(results.flatMap(r => r.contracts));
        const cMap: Record<string, Contract[]> = {};
        results.forEach(r => {
          cMap[r.projectId] = syncedContracts.filter(contract => contract.projectId === r.projectId);
        });
        setProjectContractsMap(cMap);

        const milestoneResults = await Promise.all(
          syncedContracts.map(c =>
            milestoneApi.getMilestones(c.id)
              .then(ms => ({ contractId: c.id, milestones: ms as Milestone[] }))
              .catch(() => ({ contractId: c.id, milestones: [] as Milestone[] }))
          )
        );
        const mMap: Record<string, Milestone[]> = {};
        milestoneResults.forEach(r => { mMap[r.contractId] = r.milestones; });
        setContractMilestonesMap(mMap);
      });
    }).catch(() => {});
  };
  useRefreshOnNotification(loadProjects);



  // Persist ONLY explicit user choices as a per-user setting on the server, so
  // it follows the logged-in user (not just this browser) and the automatic
  // collapse is never remembered.
  const setWelcomeCollapsedByUser = (collapsed: boolean) => {
    setWelcomeAnimate(true);
    setWelcomeCollapsed(collapsed);
    localStorage.setItem('personWelcomeWidgetCollapsedHint', String(collapsed));
    userPreferencesApi.savePreference('personWelcomeWidgetCollapsed', collapsed).catch(() => {});
  };

  // Load the logged-in user's saved preference. If they have one, respect it.
  // If not (first time this user logs in), keep the widget visible for this
  // session but remember that the user has seen it so future logins start
  // collapsed.
  useEffect(() => {
    userPreferencesApi.getPreferences().then(prefs => {
      if (typeof prefs.personWelcomeWidgetCollapsed === 'boolean') {
        // Apply WITHOUT animation so a collapsed widget stays minimized from
        // the start (no open flash).
        setWelcomeCollapsed(prefs.personWelcomeWidgetCollapsed);
        localStorage.setItem('personWelcomeWidgetCollapsedHint', String(prefs.personWelcomeWidgetCollapsed));
      } else {
        setWelcomeCollapsed(false);
        localStorage.setItem('personWelcomeWidgetCollapsedHint', 'true');
        userPreferencesApi.savePreference('personWelcomeWidgetCollapsed', true).catch(() => {});
      }
    }).catch(() => {
      setWelcomeCollapsed(false);
    });
  }, []);


  const loadCompanyInfo = async () => {
    try {
      const info = await personApi.getLinkedCompanyInfo();
      setCompanies(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    } finally {
      setIsLoading(false);
    }
  };


  // Company numbers the person has an ongoing (not completed) project with —
  // used to sort those construction companies first in "Dina Byggföretag".
  const ongoingCompanyNumbers = new Set(
    projects
      .filter(p => p.status !== 'completed')
      .map(p => (p as any).companyNumber)
      .filter((n): n is number => typeof n === 'number')
  );

  // Personal greeting header: the logged-in user's name and how many of their
  // projects are ongoing (everything except not-started and completed).
  const greetingName = user?.fullName || t('home.greeting.fallbackName', 'där');
  const ongoingProjectsCount = projects.filter(
    (p: any) => p.status !== 'not_started' && p.status !== 'completed'
  ).length;

  // "Att göra nu" — the person's active action items, derived from data this
  // page already loads (projects, contracts, milestones) plus the per-contract
  // funding state and the profile. Each row deep-links to the thing to act on.
  const personTodoHref = (todo: PersonTodo): string => {
    const base = todo.projectId ? `/person/projects/${todo.projectId}` : '/person/projects';
    switch (todo.kind) {
      case 'payment':
        return `${base}?tab=timeline&paymentPicker=open&paymentContractId=${encodeURIComponent(todo.contractId ?? '')}`;
      case 'approveMilestone':
      case 'objection':
      case 'conflict':
        return `${base}?tab=timeline`;
      case 'signContract':
      case 'reviewContract':
        // review=1 opens "Granska och signera avtal" directly.
        return `${base}?tab=contract&contractNumber=${todo.contractNumber ?? ''}&review=1`;
      case 'completeProfile':
        return '/profile';
      default:
        return base;
    }
  };
  const allContracts = Object.values(projectContractsMap).flat();
  const personTodos = getActivePersonTodos({
    projects,
    fundingActions,
    contracts: allContracts,
    milestones: Object.values(contractMilestonesMap).flat(),
    profile,
  });
  // A contract to sign that belongs to an open objection says that signing
  // closes the objection (ClickUp 869fgj7uc).
  const objections = useOngoingObjections(allContracts.filter(contract =>
    personTodos.some(todo => todo.kind === 'signContract' && todo.contractId === contract.id)
  ));
  const personTodoRows: PersonTodoRow[] = personTodos.map(todo => ({
    ...todo,
    objectionMilestoneNumber: todo.kind === 'signContract' && todo.contractId
      ? objections[todo.contractId]?.milestoneNumber
      : undefined,
    href: personTodoHref(todo),
  }));
  useReportTodoCount(isLoading ? null : personTodoRows.length);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
            {/* Personal greeting header straight on the page background, like
                the project page header: breadcrumb, "Hej {namn}" and the
                number of ongoing projects. */}
            <div className="home-greeting mb-4">
              <Breadcrumb items={[{ label: t('nav.home'), path: '/home' }]} />
              <h3 className="fw-bold mb-1 mt-2 text-break">
                {t('home.greeting.hello', { name: greetingName, defaultValue: 'Hej {{name}}' })}
              </h3>
              <p className="mb-0">
                {t('home.greeting.ongoingProjects', { count: ongoingProjectsCount, defaultValue: '{{count}} pågående projekt' })}
              </p>
            </div>

            {/* Invitations to the verified email that wait for an answer
                (ClickUp 869fmx898), above everything else. */}
            <OpenInvitations onAccepted={() => { void loadCompanyInfo(); void loadProjects(); }} />

            {/* Welcome widget: professional, trust-building first view when the
                person has no project yet. Replaces the old thin empty states and
                embeds the "invite a company" action. Replaced by the project
                overview once a project exists. */}
            {projects.length === 0 && (
              <PersonWelcomeWidget
                name={user?.fullName}
                collapsed={welcomeCollapsed}
                animate={welcomeAnimate}
                onToggleCollapsed={setWelcomeCollapsedByUser}
              />
            )}

            <div className="row">
              <div className="col-lg-8">
            {/* "Att göra nu" — the person's action list, above the project
                timeline so the next step is always the first thing seen. */}
            <SectionPanel
              id={TODO_ANCHOR}
              title={t('home.todoNow.title')}
              icon="clock"
              className="mb-4"
              fillHeight={false}
              headerAction={personTodoRows.length > 0 ? (
                <span className="badge bg-danger flex-shrink-0">
                  {t('home.todoNow.count', { count: personTodoRows.length })}
                </span>
              ) : undefined}
            >
              <PersonTodoList items={personTodoRows} />
            </SectionPanel>

            {/* Shared with company, lawyer and inspector home pages. */}
            <ProjectTimelineWidget
              className="card mb-4"
              projects={projects}
              contracts={Object.values(projectContractsMap).flat()}
              milestones={Object.values(contractMilestonesMap).flat()}
              statusSteps={projectStatusMap}
              fundingByProject={fundingByProject}
              projectHref={(project) => `/person/projects/${project.id}`}
              attestationSide="person"
              onMilestonesChanged={(contractId, updated) => setContractMilestonesMap(previous => ({ ...previous, [contractId]: updated }))}
              showCompanyBadge
            />

              </div>

              {/* Right Column - Notifications then invite (shared banner) */}
              <div className="col-lg-4">
                <NotificationsWidget />
                {/* Invite a construction company — always directly under
                    notifications, in the shared dark-blue banner design,
                    whether or not the person has a project yet. */}
                <CompanyInviteWidget banner compact collapsible showIntro={false} className="mb-4" />
                {/* "Dina Byggföretag" — last in the right column, under the invite. */}
                <LinkedCompaniesWidget
                  companies={companies}
                  ongoingCompanyNumbers={ongoingCompanyNumbers}
                  className="mb-4"
                />
              </div>
            </div>
    </DashboardLayout>
  );
}
